import Api from '@/api'
import * as types from '../mutations'
import ProductService from '@/services/products'
import BrandService from '@/services/brands'
import campaignService from '@/services/campaigns'
import ElabelService from '@/services/elabel'
import Vue from 'vue'

const state = {
  products: [],
  fullProductList: [],
  selectedCampaign: {},
  filteredProductCount: 0,
  loading: true,
  brands: [],
  defaultBrand: {},
  consumerOptions: {},
  selectedProduct: {},
  hasUnsavedChanges: false
}

const getters = {
  hasUnsavedChanges: (state) => state.hasUnsavedChanges,
  selectedProduct: (state) => state.selectedProduct,
  brands: (state) => state.brands,
  defaultBrand: (state) => state.defaultBrand,
  products: (state) => state.products,
  fullProductList: (state) => state.fullProductList,
  selectedCampaign: (state) => state.selectedCampaign,
  loadingProducts: (state) => state.loading,
  filteredProductCount: (state) => state.filteredProductCount,
  consumerOptions: (state) => state.consumerOptions,
  campaignRedirectionMode: (state) => {
    if (state.consumerOptions && state.consumerOptions.is_enabled && state.consumerOptions.consumer_url) {
      if (state.consumerOptions.consumer_url.indexOf(Api.default_stc_url) > -1) {
        return 'stc'
      } else if (state.consumerOptions.consumer_url.indexOf(Api.default_elabel_url) > -1 ||
        state.consumerOptions.consumer_url.indexOf(Api.remy_elabel_url) > -1) {
        return 'elabel'
      } else {
        return 'url'
      }
    } else if (state.consumerOptions && !state.consumerOptions.is_enabled) {
      return 'product'
    } else {
      return 'url'
    }
  }
}

const actions = {

  async searchBrands ({ commit }, { search }) {
    const res = await BrandService.loadBrands()
    return res
  },

  async loadBrands ({ commit }) {
    const brands = await BrandService.loadBrands()
    commit(types.SET_BRAND_LIST, brands)
    commit(types.SET_DEFAULT_BRAND, brands[0])
  },

  async updateProductJson ({ commit }, { id, json }) {
    const res = await ProductService.updateProductJson({ id, json })
    commit(types.SET_PRODUCT_JSON, { id, json: res })
    commit(types.SET_SELECTED_PRODUCT, id)
    return Promise.resolve(res)
  },

  async loadProductJson ({ commit }, { id }) {
    const res = await ProductService.loadProductJson(id)
    commit(types.SET_PRODUCT_JSON, { id, json: res })
    return Promise.resolve({ json: res, product: id })
  },

  async loadFullProductList ({ commit, dispatch }, offset) {
    const res = await ProductService.loadProducts({ limit: 500, offset: offset || 0 })
    if (res.count > 500 && res.results.length === 500) {
      await dispatch('loadFullProductList', offset ? offset + 500 : 500)
    }
    commit(types.SET_FULL_PRODUCT_LIST, res.results)
  },

  async loadSelectedCampaignConsumerOptions ({ commit }, campaign) {
    const res = await campaignService.loadConsumerOptions(campaign)
    commit(types.SET_CAMPAIGN_CONSUMER_OPTIONS, res)
  },

  async searchProducts ({ commit }, { campaign, offset, limit, search }) {
    const res = await ProductService.loadProducts({ campaign, offset, limit, search })
    return res.results
  },

  async loadProducts ({ commit }, { campaign, offset, limit, search }) {
    commit(types.SET_PRODUCT_LOADING, true)
    const res = await ProductService.loadProducts({ campaign, offset, limit, search })
    if (res.results) {
      res.results.forEach((product) => {
        product.code_count = 0
      })
    }
    commit(types.SET_PRODUCT_LIST, { products: res.results, count: res.count })
    commit(types.SET_PRODUCT_LOADING, false)
  },

  async loadProductBySKU ({ commit }, product) {
    try {
      const res = await ProductService.loadProducts({ sku: product.sku })
      if (res && res.results && res.results[0]) {
        return Promise.resolve(res.results[0])
      } else {
        return Promise.resolve(product)
      }
    } catch (err) {
      return Promise.reject(err)
    }
  },

  async loadProduct ({ commit, getters }, id) {
    const res = await ProductService.loadProduct(id)
    res.code_count = 0
    commit(types.UPDATE_PRODUCT, res)
    return res
  },

  async createProduct ({ commit }, { product }) {
    try {
      const res = await ProductService.createProduct(product)
      commit(types.CREATE_PRODUCT, res)
      return Promise.resolve(res)
    } catch (err) {
      err.response.request = product
      return Promise.reject(err.response)
    }
  },

  async deleteProductPicture ({ commit }, { product }) {
    try {
      const res = await ProductService.deleteProductPicture(product.id)
      commit(types.UPDATE_PRODUCT, { ...product, image: null })
      return Promise.resolve(res)
    } catch (err) {
      return Promise.reject(err.response)
    }
  },

  async updateProduct ({ commit }, { product }) {
    try {
      const res = await ProductService.updateProduct(product)
      // Keep old code count to prevent re-fetch
      res.code_count = product.code_count
      res.code = product.code ? product.code : {}
      commit(types.UPDATE_PRODUCT, res)
      commit(types.SET_SELECTED_PRODUCT, res.id)
      return Promise.resolve(res)
    } catch (err) {
      err.response.request = product
      return Promise.reject(err.response)
    }
  },

  async publishElabel ({ commit }, { product }) {
    await ElabelService.publishELabel(product.id)
    commit(types.UPDATE_ELABEL_STATUS, { product, isPublished: true })
  },

  async unpublishElabel ({ commit }, { product }) {
    await ElabelService.unpublishELabel(product.id)
    commit(types.UPDATE_ELABEL_STATUS, { product, isPublished: false })
  }

}

const mutations = {

  [types.SET_UNSAVED_CHANGES_STATUS] (state, status) {
    state.hasUnsavedChanges = status
  },

  [types.SET_PRODUCT_JSON] (state, { id, json }) {
    const index = state.products.findIndex((e) => e.id === id)
    if (~index) {
      Vue.set(state.products[index], 'stc_data', json)
    }
  },

  [types.SET_PRODUCT_LIST] (state, { products, count }) {
    state.products = products
    state.filteredProductCount = count
  },

  // updates unarchived products count
  [types.SET_PRODUCT_COUNT] (state, count) {
    state.filteredProductCount = count
  },

  [types.SET_FULL_PRODUCT_LIST] (state, products) {
    state.fullProductList = products
  },

  [types.SET_PRODUCT_LOADING] (state, loading) {
    state.loading = loading
  },

  [types.SET_SELECTED_CAMPAIGN] (state, campaign) {
    state.selectedCampaign = campaign
  },

  [types.SET_CAMPAIGN_CONSUMER_OPTIONS] (state, options) {
    state.consumerOptions = options
  },

  [types.CREATE_PRODUCT] (state, product) {
    product.code_count = 0
    state.products.unshift(product)
  },

  [types.INCREASE_PRODUCT_CODE_COUNT] (state, { product, code }) {
    const index = state.products.findIndex((e) => e.id === product)
    if (~index) {
      state.products[index].code = code
      state.products[index].code_count += 1
    }
  },

  [types.UPDATE_ELABEL_STATUS] (state, { product, isPublished }) {
    const index = state.products.findIndex((e) => e.id === product.id)
    if (~index) {
      Vue.set(state.products[index].elabel, 'is_published', isPublished)
    }
  },

  [types.SET_SELECTED_PRODUCT] (state, id) {
    const index = state.products.findIndex((e) => e.id === id)
    if (~index) {
      Vue.set(state, 'selectedProduct', state.products[index])
    }
  },

  [types.UPDATE_PRODUCT] (state, product) {
    const index = state.products.findIndex((e) => e.id === product.id)
    if (~index) {
      Vue.set(state.products, index, { ...state.products[index], ...product })
    }
  },

  [types.SET_PRODUCT_CODE_AND_COUNT] (state, { productId, count, code }) {
    const index = state.products.findIndex((e) => e.id === productId)
    if (~index) {
      Vue.set(state.products[index], 'code_count', count)
      Vue.set(state.products[index], 'code', code)
    }
  },

  [types.SET_BRAND_LIST] (state, brands) {
    state.brands = brands
  },

  [types.SET_DEFAULT_BRAND] (state, brand) {
    state.defaultBrand = brand
  }

}

export default {
  state,
  getters,
  actions,
  mutations
}
